import { useMemo } from "react";
import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { getChannels, createChannel, updateChannel, deleteChannel, getChannel, createSalesDocument, updateSalesDocument } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetChannels = (type,page,pageSize,search,filterStatus = ["active"]) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["channels", type,page,pageSize,search,filterStatus],
    queryFn: () => getChannels(type,page,pageSize,search,filterStatus),
    enabled: !!type,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetChannelsInfinite = (type, pageSize = 50, search = "") => {
  const query = useInfiniteQuery({
    queryKey: ["channels", "infinite", type, pageSize, search],
    queryFn: ({ pageParam }) => getChannels(type, pageParam, pageSize, search),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = lastPage?.pagination?.page;
      const pageCount = lastPage?.pagination?.pageCount;
      if (page != null && pageCount != null && page < pageCount) {
        return page + 1;
      }
      return undefined;
    },
    enabled: !!type,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });

  const results = useMemo(
    () => query.data?.pages?.flatMap((page) => page?.results || []) || [],
    [query.data]
  );

  return {
    ...query,
    results,
  };
};

export const useGetChannel = (id,tab) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["channel", id,tab],
    queryFn: () => getChannel(id,tab),
    enabled: !!id && !!tab,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return {
    data,
    isLoading,
    status,
    isError,
    error,
    isSuccess,
  };
};

export const useCreateChannel = (type, options = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (data) => createChannel(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["channels"] });
      
      // Show success notification
      showNotification('success', 'Channel created successfully!');
      
      if(type === 'inventory') {
        navigate('/inventory');
      } 
      if (!options.suppressNavigate) {
        navigate('/channels');
      }

    },
    onError: (error) => {
      console.error("Error creating channel:", error);
      
      // Show error notification
      let errorMessage = 'Error creating channel. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useCreateSalesDocument = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (data) => createSalesDocument(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["channels"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });
      
      // Show success notification
      showNotification('success', 'Sales document created successfully!');
      
    },
    onError: (error) => {
        console.error("Error creating sales document:", error);
      
      // Show error notification
      let errorMessage = 'Error creating sales document. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};
export const useUpdateSalesDocument = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateSalesDocument(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["channels"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });
      
      showNotification('success', 'Sales document updated successfully!');
      
    },
    onError: (error) => {
      console.error("Error updating sales document:", error);
      
      // Show error notification
      let errorMessage = 'Error updating channel. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateChannel = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateChannel(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["channels"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });
      
      // Show success notification
      showNotification('success', 'Channel updated successfully!');
      
    },
    onError: (error) => {
      console.error("Error updating channel:", error);
      
      // Show error notification
      let errorMessage = 'Error updating channel. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteChannel = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (id) => deleteChannel(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["channels"] });
      
      // Show success notification
      showNotification('success', 'Channel deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting channel:", error);
      
      // Show error notification
      let errorMessage = 'Error deleting channel. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
}; 